"use client";

import { useTranslations } from "next-intl";
import { FilterBar, ProductGrid, useProductFilters, type ProductOrPeptideCard } from "@/ui/components/plp";
import { buttonStyles } from "@/ui/components/ui/button";

interface ProductsPageClientProps {
	products: ProductOrPeptideCard[];
	pageInfo: {
		hasNextPage: boolean;
		hasPreviousPage: boolean;
		startCursor?: string | null;
		endCursor?: string | null;
	};
	totalCount?: number;
	/** Categories resolved from URL slugs (server-side) for active filter display */
	resolvedCategories?: Array<{ slug: string; id: string; name: string }>;
}

export function ProductsPageClient({
	products,
	pageInfo: _pageInfo,
	resolvedCategories = [],
}: ProductsPageClientProps) {
	const t = useTranslations("plp");
	const {
		filteredProducts,
		categoryOptions,
		colorOptions,
		sizeOptions,
		priceRanges,
		peptideCategoryOptions,
		researchLevelOptions,
		availabilityOptions,
		selectedCategories,
		selectedColors,
		selectedSizes,
		selectedPriceRange,
		searchQuery,
		selectedPeptideCategories,
		selectedResearchLevel,
		selectedAvailability,
		sortValue,
		activeFilters,
		handleCategoryToggle,
		handleColorToggle,
		handleSizeToggle,
		handlePriceRangeChange,
		handleSearchChange,
		handlePeptideCategoryToggle,
		handleResearchLevelChange,
		handleAvailabilityChange,
		handleSortChange,
		handleRemoveFilter,
		handleClearFilters,
	} = useProductFilters({
		products,
		resolvedCategories,
		enableCategoryFilter: true,
	});

	return (
		<FilterBar
			resultCount={filteredProducts.length}
			sortValue={sortValue}
			onSortChange={handleSortChange}
			categoryOptions={categoryOptions}
			colorOptions={colorOptions}
			sizeOptions={sizeOptions}
			priceRanges={priceRanges}
			peptideCategoryOptions={peptideCategoryOptions}
			researchLevelOptions={researchLevelOptions}
			availabilityOptions={availabilityOptions}
			selectedCategories={selectedCategories}
			selectedColors={selectedColors}
			selectedSizes={selectedSizes}
			selectedPriceRange={selectedPriceRange}
			searchQuery={searchQuery}
			selectedPeptideCategories={selectedPeptideCategories}
			selectedResearchLevel={selectedResearchLevel}
			selectedAvailability={selectedAvailability}
			onCategoryToggle={handleCategoryToggle}
			onColorToggle={handleColorToggle}
			onSizeToggle={handleSizeToggle}
			onPriceRangeChange={handlePriceRangeChange}
			onSearchChange={handleSearchChange}
			onPeptideCategoryToggle={handlePeptideCategoryToggle}
			onResearchLevelChange={handleResearchLevelChange}
			onAvailabilityChange={handleAvailabilityChange}
			activeFilters={activeFilters}
			onRemoveFilter={handleRemoveFilter}
			onClearFilters={handleClearFilters}
		>
			{filteredProducts.length > 0 ? (
				<ProductGrid products={filteredProducts} />
			) : (
				<div className="rounded-xl border border-border bg-card py-12 text-center">
					<p className="text-lg text-muted-foreground">{t("noResultsMatch")}</p>
					<button
						onClick={handleClearFilters}
						className={buttonStyles({
							variant: "outline-solid",
							size: "sm",
							className: "mt-4 h-9",
						})}
					>
						{t("clearFilters")}
					</button>
				</div>
			)}
			{/* Pagination entfernt — alle Produkte werden direkt angezeigt */}
		</FilterBar>
	);
}
